export type InitialTeamDecision = {
  /** The team to activate. */
  teamId: number;
  /** True when the URL names a portfolio that cannot belong to `teamId`. */
  leavePortfolio: boolean;
};

/** `/portfolios/25338`, but not the `/portfolios` list itself. */
function namesAPortfolio(pathname: string): boolean {
  return /^\/portfolios\/[^/]+/.test(pathname || '');
}

/**
 * `leavePortfolio` fires only when the URL EXPLICITLY named a team that is not ours: pairing that
 * link's portfolio with a substitute team mints a combination that never existed. A bookmark with
 * no `?team_id=` is left to `RequirePortfolioAccess`, which asks the backend instead of guessing.
 */
export function resolveInitialTeam({
  requestedTeamId,
  explicitTeamId,
  teamIds,
  pathname,
}: {
  requestedTeamId: number;
  /** `?team_id=` from the URL, if the link carried one. */
  explicitTeamId?: number | null;
  teamIds: number[];
  pathname: string;
}): InitialTeamDecision {
  if (teamIds.includes(requestedTeamId)) {
    return { teamId: requestedTeamId, leavePortfolio: false };
  }

  // Integer-and-positive, not just non-null: `?team_id=abc` decodes to NaN, which `!= null` admits
  // and `includes` rejects — bouncing a deep link whose portfolio is perfectly valid under teams[0].
  const askedForSomeoneElsesTeam =
    Number.isInteger(explicitTeamId) && explicitTeamId > 0 && !teamIds.includes(explicitTeamId);

  return { teamId: teamIds[0], leavePortfolio: askedForSomeoneElsesTeam && namesAPortfolio(pathname) };
}
